import { useEffect, useRef } from "react";
import classNames from "classnames";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { NAME_SPACES } from "shared/locales/constants";

// The colour of the rings and the picker's hover outline. Written into a
// generated stylesheet, so the SCSS layer cannot own it.
const HIGHLIGHT = "#0e93e5";
const HIGHLIGHT_WASH = "rgba(14,147,229,0.08)";

/**
 * A slot id is about to be written inside a CSS selector and a JavaScript
 * string literal, where a quote or an angle bracket stops meaning "text".
 * Detection produces plain ids like slot_004, so nothing legitimate is lost
 * by keeping only what an id is made of.
 */
const safeId = (value) => String(value || "").replace(/[^A-Za-z0-9_-]/g, "");

/**
 * The stylesheet is written between <style> tags, and the HTML parser closes
 * that element on the first "</style>" it reads — everything after it would
 * be parsed as markup. Same reasoning as the server-side sanitiser; this is
 * the other end of the same pipe, and the markup here comes from a model.
 */
const CLOSING_STYLE_TAG = /<\s*\/\s*style/gi;

const safeStyle = (css) => String(css || "").replace(CLOSING_STYLE_TAG, "");

/**
 * Rings the held slots inside the rendered screen and scrolls to the first.
 * The slot list shows an id and a truncated excerpt, which says what a slot
 * holds but never where it sits; on a long screen that is the whole question.
 * The markup already carries the anchor, so nothing has to be rewritten.
 *
 * Past one slot each ring is numbered, and that number is what ties a ring on
 * screen to a chip above the composer: without it, three identical outlines
 * say that three things are held but not which.
 */
const highlightAssets = (slotIds) => {
  const ids = (Array.isArray(slotIds) ? slotIds : [slotIds]).filter(Boolean);
  if (!ids.length) return "";
  const numbered = ids.length > 1;
  const rules = ids
    .map((id, index) => {
      const target = `[data-kayros-slot="${safeId(id)}"]`;
      const ring =
        `${target}{outline:2px solid ${HIGHLIGHT};outline-offset:3px;` +
        `background:${HIGHLIGHT_WASH};` +
        `${numbered ? "position:relative;" : ""}}`;
      if (!numbered) return ring;
      return (
        ring +
        `${target}::after{content:"${index + 1}";position:absolute;` +
        `top:-11px;right:-11px;width:20px;height:20px;border-radius:50%;` +
        `background:${HIGHLIGHT};color:#fff;font:600 11px/20px sans-serif;` +
        `text-align:center;z-index:9;}`
      );
    })
    .join("");
  return (
    `<style>${rules}</style>` +
    `<script>document.addEventListener("DOMContentLoaded",function(){` +
    `var n=document.querySelector('[data-kayros-slot="${safeId(ids[0])}"]');` +
    `if(n&&n.scrollIntoView)n.scrollIntoView({block:"center"});});</script>`
  );
};

const PICK_MESSAGE = "kayros-slot-pick";

/**
 * Makes the rendered screen answer the other half of the question: the list
 * says what a slot holds, this says which thing on screen is a slot. Clicks
 * are swallowed rather than followed — a preview is not a journey, and a
 * "Continuer" that navigated would lose the operator's place.
 *
 * A plain click replaces what was held and Ctrl/Cmd+click adds to it, as
 * everywhere else a selection exists. Accumulating on every click would mean
 * one cannot look around without committing to what one looked at.
 */
const pickerAssets = (pickable) => {
  if (!pickable) return "";
  return (
    `<style>[data-kayros-slot]{cursor:pointer;}` +
    `[data-kayros-slot]:hover{outline:1px dashed ${HIGHLIGHT};` +
    `outline-offset:2px;}` +
    `</style><script>document.addEventListener("click",function(e){` +
    `var n=e.target&&e.target.closest?e.target.closest("[data-kayros-slot]"):null;` +
    `if(!n)return;e.preventDefault();e.stopPropagation();` +
    `parent.postMessage({type:"${PICK_MESSAGE}",` +
    `additive:!!(e.ctrlKey||e.metaKey),` +
    `id:n.getAttribute("data-kayros-slot")},"*");},true);</script>`
  );
};

const DEFAULT_STYLE =
  "<style>body{margin:0;font-family:-apple-system,BlinkMacSystemFont," +
  "'Segoe UI',Roboto,sans-serif;}</style>";

const buildPreviewDocument = (
  htmlContent,
  styleContent,
  highlightSlot,
  pickable,
) => {
  const extras =
    (styleContent ? `<style>${safeStyle(styleContent)}</style>` : "") +
    highlightAssets(highlightSlot) +
    pickerAssets(pickable);
  const isFullDocument =
    htmlContent.indexOf("<!DOCTYPE") >= 0 || htmlContent.indexOf("<html") >= 0;
  if (isFullDocument) {
    if (!extras) return htmlContent;
    if (htmlContent.indexOf("</head>") >= 0) {
      return htmlContent.replace("</head>", `${extras}</head>`);
    }
    return htmlContent;
  }
  const base = styleContent ? "" : DEFAULT_STYLE;
  return `<!DOCTYPE html><html><head>${base}${extras}</head><body>${htmlContent}</body></html>`;
};

const ScreenThumbnail = ({
  html,
  styleContent,
  size = "md",
  title,
  scrollable = false,
  highlightSlot = null,
  onSlotSelect = null,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.PROCESS);
  const frameRef = useRef(null);

  // The frame can only report a click by message: it holds its own document.
  //
  // The sender is checked against this frame's own window. "message" is a
  // window-wide event, so without that check any frame on the page — starting
  // with the other previews, which render markup a model wrote — could name
  // the selected slot, and the selection is what scopes the next instruction
  // sent to the model.
  useEffect(() => {
    if (!onSlotSelect) return undefined;
    const onMessage = (event) => {
      if (!frameRef.current || event.source !== frameRef.current.contentWindow)
        return;
      const data = event.data;
      if (!data || data.type !== PICK_MESSAGE || !data.id) return;
      onSlotSelect(data.id, !!data.additive);
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [onSlotSelect]);
  return (
    <div
      className={classNames("screen-thumbnail", `screen-thumbnail--${size}`, {
        "screen-thumbnail--scrollable": scrollable,
      })}
    >
      <iframe
        ref={frameRef}
        title={title || t("STATUS_NODE.PREVIEW")}
        srcDoc={buildPreviewDocument(
          html,
          styleContent,
          highlightSlot,
          !!onSlotSelect,
        )}
        // allow-same-origin is deliberately absent. With allow-scripts it
        // would void the sandbox: the frame would share this document's
        // origin and could read the session from it — and what it renders is
        // markup a model wrote from an uploaded image. Nothing here needs it.
        // The picker talks to us through postMessage, which crosses origins,
        // and the listener identifies the sender by its window, not its host.
        sandbox="allow-scripts"
        scrolling={scrollable ? "yes" : "no"}
        tabIndex={scrollable ? 0 : -1}
        className="screen-thumbnail__frame"
      />
    </div>
  );
};
ScreenThumbnail.propTypes = {
  html: PropTypes.string.isRequired,
  styleContent: PropTypes.string,
  size: PropTypes.oneOf(["sm", "md", "lg"]),
  scrollable: PropTypes.bool,
  title: PropTypes.string,
  highlightSlot: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  onSlotSelect: PropTypes.func,
};

ScreenThumbnail.defaultProps = {
  styleContent: undefined,
  size: "md",
  scrollable: false,
  title: undefined,
  highlightSlot: null,
  onSlotSelect: null,
};

export default ScreenThumbnail;
